import { MapPin, Phone, Mail, Linkedin, Facebook, Twitter } from "lucide-react";
import Seo from "@/components/Seo";
import ContactForm from "@/components/ContactForm";
import { PageHero, SectionHead } from "@/components/Section";
import { Reveal } from "@/components/Reveal";
import { COMPANY } from "@/data/site";

export default function Contact() {
  return (
    <>
      <Seo
        title="Contact — BNM Sustainable Energy Limited"
        description="Contact BNM Sustainable Energy Limited in Waterloo, Sierra Leone. Product, partnership, investment, technology and media inquiries welcome."
      />
      <PageHero
        testid="contact-hero"
        dark={false}
        eyebrow="Contact"
        lines={["Start the", "conversation."]}
        lead="Whether you are a customer, investor, institution, researcher or journalist — the BNM team would like to hear from you."
      />

      <section data-testid="contact-section" className="bg-mist">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14">
            <div className="lg:col-span-2">
              <SectionHead eyebrow="Direct Contact" title="BNM Sustainable Energy Limited" />
              <Reveal delay={0.1}>
                <ul className="mt-8 space-y-5">
                  <li className="flex gap-4">
                    <MapPin className="h-5 w-5 mt-0.5 shrink-0 text-emerald-600" aria-hidden="true" />
                    <span className="text-sm leading-relaxed text-slate-700">
                      {COMPANY.addressLines[0]}
                      <br />
                      {COMPANY.addressLines[1]}
                    </span>
                  </li>
                  <li>
                    <a href={COMPANY.phoneHref} data-testid="contact-phone-link" className="flex gap-4 text-sm text-slate-700 hover:text-emerald-700 transition-colors">
                      <Phone className="h-5 w-5 mt-0.5 shrink-0 text-emerald-600" aria-hidden="true" />
                      {COMPANY.phoneDisplay}
                    </a>
                  </li>
                  {COMPANY.emails.map((email) => (
                    <li key={email}>
                      <a href={`mailto:${email}`} data-testid={`contact-email-${email}`} className="flex gap-4 text-sm text-slate-700 hover:text-emerald-700 transition-colors break-all">
                        <Mail className="h-5 w-5 mt-0.5 shrink-0 text-emerald-600" aria-hidden="true" />
                        {email}
                      </a>
                    </li>
                  ))}
                </ul>
                <div className="mt-8 flex items-center gap-3">
                  <a href={COMPANY.socials.linkedin} target="_blank" rel="noopener noreferrer" aria-label="BNM on LinkedIn" data-testid="contact-social-linkedin"
                    className="inline-flex h-11 w-11 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-600 hover:text-emerald-700 hover:border-emerald-400 transition-colors">
                    <Linkedin className="h-4 w-4" />
                  </a>
                  <a href={COMPANY.socials.facebook} target="_blank" rel="noopener noreferrer" aria-label="BNM on Facebook" data-testid="contact-social-facebook"
                    className="inline-flex h-11 w-11 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-600 hover:text-emerald-700 hover:border-emerald-400 transition-colors">
                    <Facebook className="h-4 w-4" />
                  </a>
                  <a href={COMPANY.socials.x} target="_blank" rel="noopener noreferrer" aria-label="BNM on X" data-testid="contact-social-x"
                    className="inline-flex h-11 w-11 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-600 hover:text-emerald-700 hover:border-emerald-400 transition-colors">
                    <Twitter className="h-4 w-4" />
                  </a>
                </div>
              </Reveal>
            </div>

            <Reveal delay={0.15} className="lg:col-span-3">
              <div className="rounded-lg border border-slate-200 bg-white p-7 sm:p-10">
                <h2 className="font-display text-xl sm:text-2xl font-bold tracking-tight text-slate-900">
                  Submit an inquiry
                </h2>
                <p className="mt-2 mb-8 text-sm text-slate-600">
                  Fields marked * are required. Your details are used only to respond to your inquiry.
                </p>
                <ContactForm />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
